<script lang="ts">
  import { untrack } from 'svelte';
  import ChatHeader from '../../lib/chat/ChatHeader.svelte';
  import { getBibliotecaPdfUrl } from '../../externalUrls';
  import { getRoute, navigate } from '../../route.svelte';
  import type { Palette } from '../../theme.svelte';
  import BibliotecaDocumentReader from './BibliotecaDocumentReader.svelte';
  import { getBibliotecaDocument, listBibliotecaDocuments } from './api';
  import type {
    BibliotecaDocumentDetail,
    BibliotecaDocumentSummary,
    BibliotecaListFilters,
    BibliotecaListResponse,
  } from './types';

  type BibliotecaPageProps = {
    onToggleSidebar: () => void;
    palette: Palette;
    isSidebarCollapsed?: boolean;
    isMobile?: boolean;
  };

  let {
    onToggleSidebar,
    palette,
    isSidebarCollapsed = false,
    isMobile = false,
  }: BibliotecaPageProps = $props();

  let route = $derived(getRoute());

  let searchQuery = $state('');
  let sourceFilter = $state('');
  let yearFilter = $state('');
  let typeFilter = $state('');
  let entityFilter = $state('');
  let numberFilter = $state('');
  let currentPage = $state(1);

  let listResponse = $state<BibliotecaListResponse | null>(null);
  let listLoading = $state(true);
  let listError = $state<string | null>(null);
  let detailDocument = $state<BibliotecaDocumentDetail | null>(null);
  let detailLoading = $state(true);
  let detailError = $state<string | null>(null);

  let listRequestVersion = 0;
  let detailRequestVersion = 0;

  let results = $derived(listResponse?.resultados ?? []);
  let availableYears = $derived(listResponse?.anios ?? []);
  let availableSources = $derived(listResponse?.fuentes ?? []);
  let detailSourceUrl = $derived(
    detailDocument ? safeExternalUrl(detailDocument.url_fuente) : null,
  );
  let detailText = $derived(
    detailDocument
      ? (detailDocument.texto_render ?? detailDocument.texto_completo ?? '').trim()
      : '',
  );

  const dateFormatter = new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'long',
    timeZone: 'UTC',
  });

  function selectedFilters(page: number): BibliotecaListFilters {
    const parsedYear = Number.parseInt(yearFilter, 10);

    return {
      q: searchQuery,
      fuente: sourceFilter,
      tipo: typeFilter,
      anio: Number.isInteger(parsedYear) ? parsedYear : null,
      entidad: entityFilter,
      numero: numberFilter,
      pagina: page,
    };
  }

  async function loadList(page = currentPage): Promise<void> {
    const requestVersion = ++listRequestVersion;
    currentPage = page;
    listLoading = true;
    listError = null;

    try {
      const response = await listBibliotecaDocuments(selectedFilters(page));
      if (requestVersion !== listRequestVersion) return;

      listResponse = response;
      currentPage = response.pagina;
    } catch (error) {
      if (requestVersion !== listRequestVersion) return;

      console.error('Unable to load Biblioteca documents:', error);
      listError = 'No se pudieron cargar los documentos. Inténtalo nuevamente.';
    } finally {
      if (requestVersion === listRequestVersion) {
        listLoading = false;
      }
    }
  }

  async function loadDocument(documentId: number): Promise<void> {
    const requestVersion = ++detailRequestVersion;
    detailLoading = true;
    detailError = null;
    detailDocument = null;

    try {
      const document = await getBibliotecaDocument(documentId);
      if (requestVersion !== detailRequestVersion) return;

      detailDocument = document;
    } catch (error) {
      if (requestVersion !== detailRequestVersion) return;

      console.error('Unable to load Biblioteca document:', error);
      detailError = 'No se pudo cargar este documento. Inténtalo nuevamente.';
    } finally {
      if (requestVersion === detailRequestVersion) {
        detailLoading = false;
      }
    }
  }

  function handleSearch(event: Event): void {
    event.preventDefault();
    void loadList(1);
  }

  function clearFilters(): void {
    searchQuery = '';
    sourceFilter = '';
    yearFilter = '';
    typeFilter = '';
    entityFilter = '';
    numberFilter = '';
    void loadList(1);
  }

  function openDocument(documentId: number): void {
    navigate(`/biblioteca/${documentId}`);
  }

  function backToResults(): void {
    navigate('/biblioteca');
  }

  function previousPage(): void {
    if (listResponse && listResponse.pagina > 1) {
      void loadList(listResponse.pagina - 1);
    }
  }

  function nextPage(): void {
    if (listResponse && listResponse.pagina < listResponse.paginas) {
      void loadList(listResponse.pagina + 1);
    }
  }

  function retryDocument(): void {
    if (route.name === 'biblioteca-document') {
      void loadDocument(route.documentId);
    }
  }

  function documentTitle(document: BibliotecaDocumentSummary | BibliotecaDocumentDetail): string {
    return document.titulo || document.epigrafe || `Documento ${document.id}`;
  }

  function formatDate(value: string | null): string {
    if (!value) return 'No disponible';

    const date = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(date.valueOf()) ? value : dateFormatter.format(date);
  }

  function safeExternalUrl(value: string): string | null {
    if (!value) return null;

    try {
      const url = new URL(value);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
    } catch {
      return null;
    }
  }

  $effect(() => {
    const activeRoute = route;

    untrack(() => {
      if (activeRoute.name === 'biblioteca') {
        void loadList();
      } else if (activeRoute.name === 'biblioteca-document') {
        void loadDocument(activeRoute.documentId);
      }
    });
  });
</script>

<svelte:head>
  <title>Biblioteca jurídica | Jurismart</title>
</svelte:head>

<div
  class="biblioteca-layout"
  style={`--biblioteca-surface: ${palette.bgCard}; --biblioteca-input: ${palette.bgChatInput}; --biblioteca-text: ${palette.textPrimary}; --biblioteca-muted: ${palette.textSecondary}; --biblioteca-border: ${palette.borderSoft}; --biblioteca-accent: ${palette.accent};`}
>
  <ChatHeader
    {onToggleSidebar}
    {palette}
    {isSidebarCollapsed}
    {isMobile}
    heading="Biblioteca jurídica"
  />

  <main class="biblioteca-content scroll-y">
    <div class="biblioteca-container">
      {#if route.name === 'biblioteca'}
        <section class="library-intro" aria-labelledby="biblioteca-description">
          <p class="eyebrow">Investigación normativa</p>
          <p id="biblioteca-description">
            Busca normas, resoluciones y publicaciones oficiales en la biblioteca jurídica.
          </p>
        </section>

        <section class="filters-panel" aria-label="Buscar y filtrar documentos">
          <form class="filter-form" onsubmit={handleSearch}>
            <div class="search-row">
              <label class="field search-field">
                <span class="field-label">Buscar documentos</span>
                <input
                  bind:value={searchQuery}
                  type="search"
                  placeholder="Título, tema, texto o número"
                  autocomplete="off"
                />
              </label>
              <button class="button button-primary" type="submit" disabled={listLoading}>
                <i class="bi bi-search" aria-hidden="true"></i>
                Buscar
              </button>
              <button class="button button-secondary" type="button" onclick={clearFilters} disabled={listLoading}>
                Limpiar
              </button>
            </div>

            <div class="filter-grid">
              <label class="field">
                <span class="field-label">Fuente</span>
                <select bind:value={sourceFilter}>
                  <option value="">Todas las fuentes</option>
                  {#each availableSources as source}
                    <option value={source.valor}>{source.nombre}</option>
                  {/each}
                </select>
              </label>

              <label class="field">
                <span class="field-label">Año</span>
                <select bind:value={yearFilter}>
                  <option value="">Todos los años</option>
                  {#each availableYears as year}
                    <option value={String(year)}>{year}</option>
                  {/each}
                </select>
              </label>

              <label class="field">
                <span class="field-label">Tipo</span>
                <input bind:value={typeFilter} type="text" placeholder="Ley, decreto…" />
              </label>

              <label class="field">
                <span class="field-label">Entidad</span>
                <input bind:value={entityFilter} type="text" placeholder="Entidad emisora" />
              </label>

              <label class="field">
                <span class="field-label">Número</span>
                <input bind:value={numberFilter} type="text" placeholder="Número de norma" />
              </label>
            </div>
          </form>
        </section>

        <section class="results-section" aria-live="polite" aria-busy={listLoading}>
          <div class="results-heading">
            <div>
              <h2>Resultados</h2>
              {#if listResponse}
                <p>
                  {listResponse.total === 1
                    ? '1 documento encontrado'
                    : `${listResponse.total} documentos encontrados`}
                </p>
              {/if}
            </div>
            {#if listLoading && listResponse}
              <span class="refreshing" role="status">Actualizando…</span>
            {/if}
          </div>

          {#if listLoading && !listResponse}
            <div class="state-panel" role="status">
              <i class="bi bi-arrow-repeat spinning" aria-hidden="true"></i>
              <p>Cargando documentos…</p>
            </div>
          {:else if listError}
            <div class="state-panel error-state" role="alert">
              <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
              <h3>No pudimos cargar los documentos</h3>
              <p>{listError}</p>
              <button class="button button-secondary" type="button" onclick={() => void loadList()}>
                Reintentar
              </button>
            </div>
          {:else if listResponse && results.length === 0}
            <div class="state-panel">
              <i class="bi bi-search" aria-hidden="true"></i>
              <h3>No encontramos documentos</h3>
              <p>Prueba con otra búsqueda o elimina algunos filtros.</p>
              <button class="button button-secondary" type="button" onclick={clearFilters}>
                Limpiar filtros
              </button>
            </div>
          {:else if listResponse}
            <div class="results-list">
              {#each results as document (document.id)}
                <button
                  class="result-card"
                  type="button"
                  onclick={() => openDocument(document.id)}
                  aria-label={`Ver ${documentTitle(document)}`}
                >
                  <span class="result-card-main">
                    <span class="result-card-meta">
                      <span>{document.fuente_nombre}</span>
                      {#if document.tipo}
                        <span>{document.tipo}</span>
                      {/if}
                      {#if document.numero}
                        <span>N.º {document.numero}</span>
                      {/if}
                      {#if document.anio}
                        <span>{document.anio}</span>
                      {/if}
                    </span>
                    <span class="result-card-title">{documentTitle(document)}</span>
                    {#if document.resumen || document.epigrafe}
                      <span class="result-card-summary">{document.resumen || document.epigrafe}</span>
                    {/if}
                    {#if document.entidad}
                      <span class="result-card-entity">{document.entidad}</span>
                    {/if}
                  </span>
                  <i class="bi bi-chevron-right result-card-arrow" aria-hidden="true"></i>
                </button>
              {/each}
            </div>

            {#if listResponse.paginas > 1}
              <nav class="pagination" aria-label="Paginación de resultados">
                <button
                  class="button button-secondary"
                  type="button"
                  disabled={listLoading || (listResponse?.pagina ?? 1) <= 1}
                  onclick={previousPage}
                >
                  <i class="bi bi-chevron-left" aria-hidden="true"></i>
                  Anterior
                </button>
                <span>Página {listResponse.pagina} de {listResponse.paginas}</span>
                <button
                  class="button button-secondary"
                  type="button"
                  disabled={listLoading || (listResponse?.pagina ?? 1) >= (listResponse?.paginas ?? 1)}
                  onclick={nextPage}
                >
                  Siguiente
                  <i class="bi bi-chevron-right" aria-hidden="true"></i>
                </button>
              </nav>
            {/if}
          {/if}
        </section>
      {:else if route.name === 'biblioteca-document'}
        <section class="detail-section" aria-busy={detailLoading}>
          <button class="back-link" type="button" onclick={backToResults}>
            <i class="bi bi-arrow-left" aria-hidden="true"></i>
            Volver a resultados
          </button>

          {#if detailLoading}
            <div class="state-panel" role="status">
              <i class="bi bi-arrow-repeat spinning" aria-hidden="true"></i>
              <p>Cargando documento…</p>
            </div>
          {:else if detailError}
            <div class="state-panel error-state" role="alert">
              <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
              <h2>No pudimos cargar el documento</h2>
              <p>{detailError}</p>
              <button class="button button-secondary" type="button" onclick={retryDocument}>
                Reintentar
              </button>
            </div>
          {:else if detailDocument}
            <article class="document-detail">
              <div class="document-header">
                <div>
                  <p class="eyebrow">{detailDocument.fuente_nombre}</p>
                  <h2>{documentTitle(detailDocument)}</h2>
                  {#if detailDocument.epigrafe && detailDocument.epigrafe !== detailDocument.titulo}
                    <p class="document-epigrafe">{detailDocument.epigrafe}</p>
                  {/if}
                </div>
                <div class="document-actions">
                  {#if detailSourceUrl}
                    <a
                      class="button button-secondary"
                      href={detailSourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                      Ver fuente
                    </a>
                  {/if}
                  <a class="button button-primary" href={getBibliotecaPdfUrl(detailDocument.id)}>
                    <i class="bi bi-file-earmark-pdf" aria-hidden="true"></i>
                    {detailDocument.tiene_pdf ? 'Descargar PDF' : 'Generar PDF'}
                  </a>
                </div>
              </div>

              <dl class="metadata-grid">
                <div>
                  <dt>Fuente</dt>
                  <dd>{detailDocument.fuente_nombre}</dd>
                </div>
                {#if detailDocument.tipo}
                  <div>
                    <dt>Tipo</dt>
                    <dd>{detailDocument.tipo}</dd>
                  </div>
                {/if}
                {#if detailDocument.numero}
                  <div>
                    <dt>Número</dt>
                    <dd>{detailDocument.numero}</dd>
                  </div>
                {/if}
                {#if detailDocument.anio}
                  <div>
                    <dt>Año</dt>
                    <dd>{detailDocument.anio}</dd>
                  </div>
                {/if}
                <div>
                  <dt>Fecha de publicación</dt>
                  <dd>{formatDate(detailDocument.fecha_publicacion)}</dd>
                </div>
                {#if detailDocument.entidad}
                  <div>
                    <dt>Entidad</dt>
                    <dd>{detailDocument.entidad}</dd>
                  </div>
                {/if}
                {#if detailDocument.estado}
                  <div>
                    <dt>Estado</dt>
                    <dd>{detailDocument.estado}</dd>
                  </div>
                {/if}
                <div>
                  <dt>Extensión</dt>
                  <dd>{detailDocument.caracteres.toLocaleString('es-CO')} caracteres</dd>
                </div>
              </dl>

              <section class="document-text-section" aria-labelledby="texto-completo-heading">
                <div class="document-text-heading">
                  <h3 id="texto-completo-heading">Texto completo</h3>
                  {#if detailDocument.normalizado}
                    <span class="document-format-badge" title="Texto estructurado en títulos, capítulos y artículos">
                      <i class="bi bi-list-nested" aria-hidden="true"></i>
                      Documento estructurado
                    </span>
                  {/if}
                </div>
                {#if detailText}
                  <BibliotecaDocumentReader content={detailText} {palette} />
                {:else}
                  <p class="empty-document-text">Este documento no tiene texto disponible.</p>
                {/if}
              </section>
            </article>
          {/if}
        </section>
      {/if}
    </div>
  </main>
</div>

<style>
  .biblioteca-layout {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    width: 100%;
    overflow: hidden;
    color: var(--biblioteca-text);
  }

  .biblioteca-content {
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow-x: hidden;
    padding: 16px 32px 32px;
  }

  .biblioteca-container {
    width: min(1120px, 100%);
    margin: 0 auto;
  }

  .library-intro {
    margin-bottom: 16px;
  }

  .library-intro p:last-child {
    max-width: 720px;
    margin: 3px 0 0;
    color: var(--biblioteca-muted);
    font-size: 14px;
  }

  .eyebrow {
    margin: 0;
    color: var(--biblioteca-accent);
    font-size: 11px;
    font-weight: 750;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .filters-panel,
  .result-card,
  .document-detail,
  .state-panel {
    border: 1px solid var(--biblioteca-border);
    border-radius: 14px;
    background: var(--biblioteca-surface);
  }

  .filters-panel {
    padding: 16px;
  }

  .filter-form {
    display: grid;
    gap: 14px;
  }

  .search-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    gap: 8px;
    align-items: end;
  }

  .filter-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
  }

  .field {
    display: grid;
    min-width: 0;
    gap: 5px;
  }

  .field-label {
    color: var(--biblioteca-muted);
    font-size: 12px;
    font-weight: 650;
  }

  .field input,
  .field select {
    width: 100%;
    min-width: 0;
    min-height: 38px;
    border: 1px solid var(--biblioteca-border);
    border-radius: 8px;
    background: var(--biblioteca-input);
    color: var(--biblioteca-text);
    padding: 8px 10px;
    font: inherit;
    font-size: 13px;
  }

  .field input::placeholder {
    color: var(--biblioteca-muted);
    opacity: 0.72;
  }

  .field input:focus,
  .field select:focus,
  .button:focus-visible,
  .back-link:focus-visible,
  .result-card:focus-visible {
    outline: 2px solid var(--biblioteca-accent);
    outline-offset: 2px;
  }

  .button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 38px;
    border: 1px solid transparent;
    border-radius: 8px;
    padding: 8px 12px;
    font: inherit;
    font-size: 13px;
    font-weight: 650;
    line-height: 1.2;
    text-decoration: none;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease;
  }

  .button:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  .button:disabled {
    cursor: wait;
    opacity: 0.58;
  }

  .button-primary {
    border-color: var(--biblioteca-accent);
    background: var(--biblioteca-accent);
    color: #fff;
  }

  .button-secondary {
    border-color: var(--biblioteca-border);
    background: transparent;
    color: var(--biblioteca-text);
  }

  .results-section {
    margin-top: 24px;
  }

  .results-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 10px;
  }

  .results-heading h2,
  .document-header h2,
  .state-panel h2,
  .state-panel h3,
  .document-text-section h3 {
    margin: 0;
  }

  .results-heading h2 {
    font-size: 18px;
    line-height: 1.25;
  }

  .results-heading p {
    margin: 3px 0 0;
    color: var(--biblioteca-muted);
    font-size: 13px;
  }

  .refreshing {
    color: var(--biblioteca-muted);
    font-size: 12px;
    white-space: nowrap;
  }

  .results-list {
    display: grid;
    gap: 10px;
  }

  .result-card {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    padding: 15px 16px;
    color: var(--biblioteca-text);
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  }

  .result-card:hover {
    border-color: var(--biblioteca-accent);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  .result-card-main {
    display: grid;
    min-width: 0;
    gap: 5px;
  }

  .result-card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 5px 8px;
    color: var(--biblioteca-muted);
    font-size: 11px;
    font-weight: 650;
  }

  .result-card-meta span + span::before {
    margin-right: 8px;
    color: var(--biblioteca-border);
    content: '•';
  }

  .result-card-title {
    overflow: hidden;
    color: var(--biblioteca-text);
    font-size: 15px;
    font-weight: 700;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .result-card-summary,
  .result-card-entity {
    overflow: hidden;
    color: var(--biblioteca-muted);
    font-size: 13px;
    line-height: 1.45;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .result-card-entity {
    font-size: 12px;
    font-weight: 600;
  }

  .result-card-arrow {
    flex: 0 0 auto;
    margin-left: 16px;
    color: var(--biblioteca-accent);
  }

  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 18px;
    color: var(--biblioteca-muted);
    font-size: 13px;
  }

  .state-panel {
    display: grid;
    justify-items: center;
    gap: 8px;
    padding: 32px 20px;
    text-align: center;
  }

  .state-panel > i {
    color: var(--biblioteca-accent);
    font-size: 22px;
  }

  .state-panel p {
    max-width: 460px;
    margin: 0;
    color: var(--biblioteca-muted);
    font-size: 14px;
  }

  .error-state > i,
  .error-state h2,
  .error-state h3 {
    color: #d9534f;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  .detail-section {
    display: grid;
    gap: 14px;
  }

  .back-link {
    display: inline-flex;
    justify-self: start;
    align-items: center;
    gap: 7px;
    border: 0;
    background: transparent;
    color: var(--biblioteca-accent);
    padding: 4px 0;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }

  .document-detail {
    overflow: hidden;
  }

  .document-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    padding: 22px;
    border-bottom: 1px solid var(--biblioteca-border);
  }

  .document-header h2 {
    margin-top: 5px;
    font-size: clamp(20px, 3vw, 28px);
    line-height: 1.18;
    letter-spacing: -0.015em;
  }

  .document-epigrafe {
    max-width: 800px;
    margin: 10px 0 0;
    color: var(--biblioteca-muted);
    line-height: 1.5;
  }

  .document-actions {
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    gap: 8px;
  }

  .metadata-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1px;
    margin: 0;
    border-bottom: 1px solid var(--biblioteca-border);
    background: var(--biblioteca-border);
  }

  .metadata-grid > div {
    min-width: 0;
    padding: 13px 16px;
    background: var(--biblioteca-input);
  }

  .metadata-grid dt {
    margin-bottom: 3px;
    color: var(--biblioteca-muted);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .metadata-grid dd {
    overflow-wrap: anywhere;
    margin: 0;
    color: var(--biblioteca-text);
    font-size: 13px;
    line-height: 1.4;
  }

  .document-text-section {
    padding: 22px;
  }

  .document-text-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
  }

  .document-text-heading h3 {
    margin: 0;
    font-size: 16px;
  }

  .document-format-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--biblioteca-border);
    border-radius: 999px;
    padding: 3px 10px;
    color: var(--biblioteca-muted);
    font-size: 11px;
    font-weight: 650;
    white-space: nowrap;
  }

  .empty-document-text {
    margin: 0;
    color: var(--biblioteca-muted);
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 1000px) {
    .filter-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .metadata-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 720px) {
    .biblioteca-content {
      padding: 12px 16px 24px;
    }

    .search-row {
      grid-template-columns: 1fr 1fr;
    }

    .search-field {
      grid-column: 1 / -1;
    }

    .filter-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .document-header {
      flex-direction: column;
      gap: 16px;
      padding: 18px;
    }

    .metadata-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .document-text-section {
      padding: 18px;
    }
  }

  @media (max-width: 480px) {
    .biblioteca-content {
      padding: 10px 8px max(16px, env(safe-area-inset-bottom));
    }

    .filters-panel {
      padding: 12px;
    }

    .filter-grid,
    .metadata-grid {
      grid-template-columns: 1fr;
    }

    .results-heading {
      align-items: flex-start;
      flex-direction: column;
      gap: 5px;
    }

    .result-card {
      padding: 13px;
    }

    .result-card-title,
    .result-card-summary,
    .result-card-entity {
      white-space: normal;
    }

    .pagination {
      flex-wrap: wrap;
      gap: 8px;
    }

    .document-actions {
      width: 100%;
    }

    .document-actions .button {
      flex: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .button,
    .result-card {
      transition: none;
    }

    .spinning {
      animation: none;
    }
  }
</style>
